import React from 'react'
import { analysesUpdates, useAnalysesProvider } from '../../../Providers/AnalysesProvider';
import styled from "styled-components";
import { selectedLayoutGroupAtom } from '../../../Pages/Data/Visualize/DataReview/Atoms/Layout';
import { useRecoilValue } from "recoil"
import { useOnMount } from '../../../Hooks/useOnMount'
import { useAsyncTask } from "../../../Hooks/useAsyncTask"
import { MobergBoxShadow, MobergButton, MobergButtonShape, MobergFontFamily, MobergIconSize, MobergRow } from '../../../Moberg'
import { MdAdd, MdPhotoCamera, MdIosShare } from 'react-icons/md';
import { SOFTWARE_VERSION } from '../../../Version'
import { useLayoutService } from "../../../Hooks/useLayoutService"
import { useScreenshots } from "../../../Hooks/useScreenshots"
import { useModalProvider } from "../../../Providers/ModalProvider"
import { useEnvironmentVariablesProvider } from "../../../Providers/EnvironmentVariablesProvider"
import ScreenshotExportDialog from "./ScreenshotExportDialog"
import { SortableTabs } from "./SortableTabs"
import { DataReviewDisplayGroupsDropdown } from '../../../Components/DataReviewDropdownMenu/DataReviewDisplayGroupsDropdown';
import { useDataReviewPermissions } from '../../../Hooks/useDataReviewPermissions'
import { permissionTooltip } from '../../../Utils/permissionUtils'
import { ThemeToggle } from '../../../Components/ThemeToggle/ThemeToggle';
import { ScreenshotViewer } from './ScreenshotViewer'

function Footer() {
	const analysesProvider = useAnalysesProvider()
	const selectedLayoutGroup = useRecoilValue(selectedLayoutGroupAtom)
	const { addLayoutToLayoutGroup } = useLayoutService()
	const addLayoutTask = useAsyncTask(() => addLayoutToLayoutGroup(selectedLayoutGroup))
	const { takingScreenshot, takeScreenshot } = useScreenshots()
	const { createModal, close } = useModalProvider()
	const { environmentVariables } = useEnvironmentVariablesProvider()
	const { canEditGroup } = useDataReviewPermissions()
	const canEditDisplayGroup = canEditGroup(selectedLayoutGroup?.type)

	useOnMount(() => {
		analysesProvider.update(analysesUpdates.ANCILLARY_FILTER)
	})

	const hasMaxDisplays = selectedLayoutGroup?.layouts.length > 9

	const handleScreenshotClick = () => {
		createModal(
			<ScreenshotExportDialog
				onExport={takeScreenshot}
				onClose={close}
			/>
		)
	}

	const openScreenshotExportModal = () => {
		createModal(<ScreenshotViewer />)
	}

	const addDisabledReason = permissionTooltip(canEditDisplayGroup)
		|| (addLayoutTask.isWaiting ? "Adding display..." : "")
		|| (hasMaxDisplays ? "There cannot be more than 10 different displays." : "")

	return (<>
		<div style={{ boxShadow: MobergBoxShadow.REGULAR, padding: "8px 16px", background: "var(--color-surface-panel)" }}>
			<MobergRow gap="16px">
				<DataReviewDisplayGroupsDropdown />

				<HorizontalScrollBar style={{ overflowX: "auto", overflowY: "hidden", whiteSpace: "nowrap" }}>
					<SortableTabs />
				</HorizontalScrollBar>

				{selectedLayoutGroup && (
					<MobergButton
						shape={MobergButtonShape.SQUARE}
						onClick={addLayoutTask.run}
						disabled={!!addDisabledReason}
						tooltip={addDisabledReason}
					>
						<MdAdd size={MobergIconSize.REGULAR} />
					</MobergButton>
				)}

				<MobergRow expand={false} gap="8px" style={{ flex: 1, justifyContent: "right" }}>
						{environmentVariables.FEATURE_FLAG_SCREENSHOTS && (
							<MobergButton
								shape={MobergButtonShape.SQUARE}
								onClick={handleScreenshotClick}
								disabled={takingScreenshot}
								tooltip={takingScreenshot ? "Taking screenshot..." : "Take screenshot"}
							>
								<MdPhotoCamera size={MobergIconSize.REGULAR} />
							</MobergButton>
						)}
						{environmentVariables.FEATURE_FLAG_SCREENSHOTS && (
							<MobergButton
								shape={MobergButtonShape.SQUARE}
								onClick={openScreenshotExportModal}
								disabled={takingScreenshot}
								tooltip="Export"
							>
								<MdIosShare size={MobergIconSize.REGULAR} />
							</MobergButton>
						)}
						{environmentVariables?.FEATURE_FLAG_DARK_MODE && <ThemeToggle />}
						<VersionLabel>v{SOFTWARE_VERSION}</VersionLabel>
					</MobergRow>
				</MobergRow>
		</div>
	</>)
}

const HorizontalScrollBar = styled.div`
	::-webkit-scrollbar {
		display: block;
		height: 8px;
		color: var(--color-scrollbar-thumb);
		border-radius: 2px;
	}
	
	::-webkit-scrollbar-track {
		background: var(--color-scrollbar-track);
		border-radius: 2px;
	}
`;

const VersionLabel = styled.span`
	font-family: ${MobergFontFamily.HEADER};
	font-size: 11px;
	font-weight: 600;
	color: var(--color-text-version);
	white-space: nowrap;
`;

export default Footer;